import qs from 'query-string';
import { Map, List } from 'immutable';
import styled from '@emotion/styled';
import useReactRouter from 'use-react-router';
import React, { useState, useEffect, useMemo } from 'react';
import { stringKey, arrayKey, flatten } from '~/utils/Immutable';
import { flow, omit, curry, get as lodashGet } from 'lodash';

import EditorBody from './Editor/Body';
import EditorHeader from './Editor/Header';
import { createRestApi } from '~/App/Api/rest';
import MessageTemplatesApi from '~/App/Api/MessageTemplates';
import useUnloadPagePrompt from '~/react-hooks/useUnloadPagePrompt';
import { UserSimple as User, MessageTemplate } from '~/types/models';
import { AttributeListValue, AttributeValue } from '../Types';
import { EmbedService } from '../Types';
import EditorContext, { Metadata, EditorContextProps } from './Editor/Context';
import {
  buildTemplateProgram,
  templateProgramToString,
  templateProgramToReact,
} from '~/utils/templating/compiler';
import { MessageTemplateLoading } from '~/App/Modules/Settings/MessageTemplates/Pages/Editor/Loading';

declare global {
  interface Window {
    $METADATA: Metadata;
  }
}

export enum TemplateState {
  Locked = 'locked',
  Demo = 'demo',
  Open = 'open',
}

export enum PreviewValue {
  Desktop = 'desktop',
  Mobile = 'mobile',
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const templateData = {
  email: 'joro@bonjoro.com',
  first_name: 'Tom',
  last_name: 'Hanks',
  full_name: 'Tom Hanks',
};

function cleanValue(value: string | unknown) {
  if (typeof value === 'string' && value.trim().length === 0) {
    return null;
  }

  return value;
}

const isValidObject = (value) =>
  typeof value === 'object' && value != null && !List.isList(value);

const omitKeys = ['landing_page.background', 'landing_page.logo'];

export default function Editor() {
  const [user, setUser] = useState<User>();
  const [metadata, setMetadata] = useState<Metadata>();
  const [embedServices, setEmbedServices] = useState<List<EmbedService>>(
    List([])
  );
  const [loading, setLoading] = useState(true);
  const [original, setOriginal] = useState<
    Map<string, AttributeValue | AttributeListValue>
  >(Map());
  const [attributes, setAttributes] = useState<
    Map<string, AttributeValue | AttributeListValue>
  >(Map());
  const query = qs.parse(location.search) as { preview?: PreviewValue };
  const preview = useMemo(
    () => lodashGet(query, 'preview', PreviewValue.Desktop),
    [query]
  );
  const { match } = useReactRouter<{ id: string }>();

  const usersApi = useMemo(() => createRestApi<User>('/api/v2/users'), []);
  const embedsApi = useMemo(
    () =>
      createRestApi<EmbedService>('/api/v3/message-templates/embeds/services'),
    []
  );

  const hasChanges = useMemo(
    () => attributes != null && !(attributes || Map()).equals(original),
    [attributes, original]
  );
  const [hasUpdated, setHasUpdated] = useState(false);

  // Use native prompt when message template form is updated and unsaved
  useUnloadPagePrompt(hasChanges);

  const set = curry(
    (
      key: string | string[],
      value: string | number | undefined | null | object
    ) => {
      const cleanedValue = cleanValue(value);
      const sKey = stringKey(key);
      const aKey = arrayKey(key);

      setAttributes(
        attributes.withMutations((attributes) => {
          if (isValidObject(cleanedValue)) {
            const cleanedObject = cleanApiState(flatten(cleanedValue));
            Object.keys(cleanedObject).forEach((subKey) => {
              attributes.set(stringKey([key, subKey]), cleanedObject[subKey]);
            });
          } else {
            attributes.set(sKey, cleanedValue as AttributeValue);
          }

          // flattened state may have had a null valued key
          // for an object path so we make sure it gets removed
          while (aKey.length > 0) {
            aKey.pop();

            if (aKey.length > 0) attributes.delete(stringKey(aKey));
          }
        })
      );
    }
  );

  const setCta = ({ text, uri }: { text?: string; uri?: string }) => {
    const ctaMap = {
      text: text,
      uri: uri,
    };
    set('landing_page.cta', ctaMap);
  };

  function get<T extends AttributeValue | AttributeListValue>(
    key: string | string[],
    defaultValue?: T
  ): T | undefined {
    return (attributes || Map()).get(stringKey(key), defaultValue) as T;
  }

  function cleanApiState(data: Record<string, string | unknown>) {
    const keys = Object.keys(data).filter((key) => {
      if (
        key.endsWith('created_at') ||
        key.endsWith('updated_at') ||
        key.startsWith('stats') ||
        omitKeys.some((omitKey) => omitKey === key) ||
        data[key] == null
      ) {
        return true;
      }
    });

    return omit(data, keys);
  }

  /**
   * Convert res to a flattened Map object
   * that can be compared with a Map object
   *
   * res = {
   *  id: 1234,
   *  theme: {
   *    id: 456,
   *    primary_color: #ffffff
   *  },
   *  embeds: [
   *    {
   *      id: 1234
   *      type: video
   *      model: {
   *        id: 1234
   *      }
   *    }
   *  ]
   * }
   *
   * transformed = Map({
   *  id: 1234,
   *  theme.id: 456,
   *  theme.primary_color,
   *  embeds: List(
   *    Map({
   *      id: 1234,
   *      type: vide0
   *      model.id: 1234
   *    })
   *  )
   * })
   */
  const transformData = (res: MessageTemplate) => {
    const keysWithArray = Object.keys(res).filter((key) =>
      Array.isArray(res[key])
    );
    const simpleAttribute = omit(res, keysWithArray);
    // All parent properties that are not an array will flattened
    let data = Map<string, AttributeValue | AttributeListValue>(
      cleanApiState(flatten(simpleAttribute))
    );

    // Parent properties that contain an Array
    // will be converted to a List object
    // Each item inside the List will be a flattened Map object
    keysWithArray.forEach((key) => {
      const list: Map<string, AttributeValue>[] = (
        res[key] as Array<unknown>
      ).map((item) => {
        const flattened = flatten(item);
        const emptyKeys = Object.keys(flattened).filter(
          (flatKey) => flattened[flatKey] == null
        );
        return Map(omit(flattened, emptyKeys));
      });
      data = data.update(key, () => List(list));
    });

    return data;
  };

  const setApiState = (res: MessageTemplate) => {
    const original = transformData(res);

    const updatedAttributes =
      attributes == null
        ? original
        : (original
            .mergeWith((newValue, oldValue) => {
              if (newValue == null) {
                return oldValue;
              }

              return newValue;
            }, attributes)
            .filter((value) => value != null) as Map<string, string>);

    setAttributes(updatedAttributes);
    setOriginal(original);
  };

  useEffect(() => {
    // reset state
    setAttributes(Map());
    setOriginal(Map());

    async function loadMessageTemplate() {
      const res = await MessageTemplatesApi.get(match.params.id);
      setApiState(res.data);
    }

    const loadEmbedsList = async () => {
      const services = await embedsApi.getAll();
      setEmbedServices(List(services.data));
    };

    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadMessageTemplate(), loadEmbedsList()]);
      setLoading(false);
    };

    loadData();
  }, [match.params.id]);

  useEffect(() => {
    async function loadUser() {
      setUser(await usersApi.get('me'));
    }

    loadUser();
    setMetadata(window.$METADATA);
  }, []);

  const context: EditorContextProps = useMemo(() => {
    const compileStringTemplate = flow(
      buildTemplateProgram,
      templateProgramToString(templateData)
    );
    const compileReactTemplate = flow(
      buildTemplateProgram,
      templateProgramToReact(templateData)
    );

    return {
      user,
      metadata,
      state: attributes || Map<string, unknown>(),
      set,
      setCta,
      preview,
      get,
      hasChanges,
      hasUpdated,
      setHasUpdated,
      setApiState,
      embedServices,
      compiledTemplates: {
        subjectLine: compileStringTemplate(
          get('email_template.subject_line') ??
            metadata?.defaultSubjectLine ??
            ''
        ),
        message: compileReactTemplate(
          get('email_template.message_template') ??
            metadata?.defaultMessage ??
            ''
        ),
      },
    };
  }, [user, metadata, attributes, preview, hasChanges, embedServices]);

  if (loading) {
    return <MessageTemplateLoading />;
  }

  return (
    <EditorContext.Provider value={context}>
      <Container>
        <EditorHeader />
        <EditorBody />
      </Container>
    </EditorContext.Provider>
  );
}
